import { StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { G, Text as SvgText } from 'react-native-svg'
import { StackedBarChart } from 'react-native-svg-charts'
import { useSemanticColors } from '@/constants/SemanticColors'
import type { StackedHoursItem } from '@/hooks/useAnalyticsData'
import { formatHours } from '@/lib/dates'

interface HoursBarChartProps {
    items: StackedHoursItem[]
    maxItems?: number
}

const KEYS: ('approved' | 'unapproved')[] = ['approved', 'unapproved']
const BAR_HEIGHT = 28
const BAR_GAP = 8
const MIN_SEGMENT_WIDTH = 30

interface BarLabelsProps {
    x?: (value: number) => number
    y?: (index: number) => number
    bandwidth?: number
    data?: StackedHoursItem[]
}

function BarLabels({ x, y, bandwidth, data }: BarLabelsProps) {
    if (!x || !y || !bandwidth || !data) return null

    return (
        <G>
            {data.map((item, index) => {
                const barY = y(index)
                const centerY = barY + bandwidth / 2

                const segments: { type: string; value: number; x0: number; x1: number }[] = []
                if (item.approved > 0) {
                    segments.push({
                        type: 'approved',
                        value: item.approved,
                        x0: x(0),
                        x1: x(item.approved),
                    })
                }
                if (item.unapproved > 0) {
                    segments.push({
                        type: 'unapproved',
                        value: item.unapproved,
                        x0: x(item.approved),
                        x1: x(item.approved + item.unapproved),
                    })
                }

                return segments.map(seg => {
                    const segWidth = seg.x1 - seg.x0
                    if (segWidth < MIN_SEGMENT_WIDTH) return null
                    const centerX = seg.x0 + segWidth / 2
                    return (
                        <SvgText
                            key={`${item.label}-${seg.type}`}
                            x={centerX}
                            y={centerY}
                            fontSize={10}
                            fill="white"
                            fontWeight="bold"
                            textAnchor="middle"
                            alignmentBaseline="central"
                        >
                            {formatHours(seg.value, 1)}
                        </SvgText>
                    )
                })
            })}
        </G>
    )
}

export function HoursBarChart({ items, maxItems = 10 }: HoursBarChartProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const colors = [semanticColors.statusApproved, semanticColors.warning]

    const sliced = items.slice(0, maxItems)

    if (sliced.length === 0) {
        return (
            <View style={styles.empty}>
                <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                    No data available
                </Text>
            </View>
        )
    }

    const chartHeight = sliced.length * (BAR_HEIGHT + BAR_GAP)

    return (
        <View>
            <View style={styles.chartRow}>
                <View style={styles.labels}>
                    {sliced.map(item => {
                        const label =
                            item.label.length > 16 ? `${item.label.slice(0, 15)}…` : item.label
                        return (
                            <View
                                key={item.label}
                                style={[styles.labelCell, { height: BAR_HEIGHT + BAR_GAP }]}
                            >
                                <Text
                                    variant="labelSmall"
                                    numberOfLines={1}
                                    style={{ color: theme.colors.onSurfaceVariant }}
                                >
                                    {label}
                                </Text>
                            </View>
                        )
                    })}
                </View>
                <StackedBarChart
                    style={{ flex: 1, height: chartHeight }}
                    keys={KEYS}
                    colors={colors}
                    data={sliced}
                    horizontal={true}
                    contentInset={{ top: BAR_GAP / 2, bottom: BAR_GAP / 2 }}
                    spacingInner={0.25}
                >
                    <BarLabels />
                </StackedBarChart>
            </View>
            <View style={styles.legend}>
                <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: colors[0] }]} />
                    <Text variant="labelSmall" style={{ color: theme.colors.onSurfaceVariant }}>
                        Approved
                    </Text>
                </View>
                <View style={styles.legendItem}>
                    <View style={[styles.legendDot, { backgroundColor: colors[1] }]} />
                    <Text variant="labelSmall" style={{ color: theme.colors.onSurfaceVariant }}>
                        Unapproved
                    </Text>
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    empty: {
        padding: 24,
        alignItems: 'center',
    },
    chartRow: {
        flexDirection: 'row',
    },
    labels: {
        width: 120,
        justifyContent: 'flex-start',
    },
    labelCell: {
        justifyContent: 'center',
        paddingRight: 6,
        alignItems: 'flex-end',
    },
    chart: {
        flex: 1,
    },
    legend: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 16,
        paddingTop: 8,
    },
    legendItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    legendDot: {
        width: 10,
        height: 10,
        borderRadius: 2,
    },
})
